import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import type { Module, ActionContext } from "vuex";
import type {
  Slideshow,
  SlideshowDoc,
  SlideshowSlide,
} from "@/models/interfaces";
import { deepStripUndefined } from "@/utils/firestoreClean";

export interface SlideshowsState {
  accountId: number | null;
  fetchedAccountId: number | null;
  items: Slideshow[];
  fetching: boolean;
  error: string | null;
}

type RootState = unknown;

// Sorted on the client: filtering on account and ordering by title together
// would need a composite index.
const byTitle = (a: Slideshow, b: Slideshow) =>
  a.title.localeCompare(b.title, undefined, {
    sensitivity: "base",
    numeric: true,
  });

const state = (): SlideshowsState => ({
  accountId: null,
  fetchedAccountId: null,
  items: [],
  fetching: false,
  error: null,
});

const mutations = {
  SET_ACCOUNT_ID(s: SlideshowsState, id: number | null) {
    s.accountId = id;
  },
  SET_FETCHED_ACCOUNT_ID(s: SlideshowsState, id: number | null) {
    s.fetchedAccountId = id;
  },
  SET_FETCHING(s: SlideshowsState, v: boolean) {
    s.fetching = v;
  },
  SET_ERROR(s: SlideshowsState, msg: string | null) {
    s.error = msg;
  },
  SET_ITEMS(s: SlideshowsState, items: Slideshow[]) {
    s.items = [...items].sort(byTitle);
  },
  UPSERT_ITEM(s: SlideshowsState, slideshow: Slideshow) {
    s.items = [
      ...s.items.filter((item) => item.id !== slideshow.id),
      slideshow,
    ].sort(byTitle);
  },
  REMOVE_ITEM(s: SlideshowsState, id: string) {
    s.items = s.items.filter((item) => item.id !== id);
  },
};

// A slideshow only points at its images: they live in the image library
// (the slideImages module), so that several slideshows can show the same one.
// Nothing here uploads or deletes a file.
const actions = {
  setAccountId(
    { state, commit }: ActionContext<SlideshowsState, RootState>,
    id: number | string | null | undefined,
  ) {
    // Some user docs store the account as a string.
    const accountId = id == null || id === "" ? null : Number(id);
    if (state.accountId !== accountId) {
      commit("SET_ACCOUNT_ID", accountId);
      commit("SET_FETCHED_ACCOUNT_ID", null);
      commit("SET_ITEMS", []);
    }
  },

  async fetchAll(
    { state, commit }: ActionContext<SlideshowsState, RootState>,
    options: { force?: boolean } = {},
  ) {
    if (state.accountId == null) {
      commit("SET_ITEMS", []);
      return;
    }
    if (
      !options.force &&
      (state.fetching || state.fetchedAccountId === state.accountId)
    )
      return;

    const accountId = state.accountId;
    commit("SET_FETCHING", true);
    commit("SET_ERROR", null);
    try {
      const snap = await getDocs(
        query(collection(db, "slideshows"), where("account", "==", accountId)),
      );
      // The account may have changed while the query was in flight.
      if (state.accountId !== accountId) return;

      const slideshows: Slideshow[] = snap.docs.map((d) => {
        const data = d.data() as DocumentData;
        return {
          id: d.id,
          account: data.account as number,
          title: (data.title as string) ?? "",
          slides: Array.isArray(data.slides) ? data.slides : [],
          createdBy: (data.createdBy as string) ?? "",
          updatedAt: data.updatedAt,
        };
      });
      commit("SET_ITEMS", slideshows);
      commit("SET_FETCHED_ACCOUNT_ID", accountId);
    } catch (err: any) {
      commit("SET_ERROR", err?.message ?? String(err));
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  async create(
    { state, commit }: ActionContext<SlideshowsState, RootState>,
    payload: { title: string; slides: SlideshowSlide[]; createdBy: string },
  ): Promise<Slideshow> {
    if (state.accountId == null) throw new Error("No accountId set");
    const docData: SlideshowDoc = deepStripUndefined({
      account: state.accountId,
      title: payload.title.trim(),
      slides: payload.slides,
      createdBy: payload.createdBy,
    });
    const ref = await addDoc(collection(db, "slideshows"), {
      ...docData,
      updatedAt: serverTimestamp(),
    });
    const slideshow: Slideshow = { id: ref.id, ...docData };
    commit("UPSERT_ITEM", slideshow);
    return slideshow;
  },

  async update(
    { state, commit }: ActionContext<SlideshowsState, RootState>,
    payload: { id: string; title: string; slides: SlideshowSlide[] },
  ): Promise<Slideshow> {
    const existing = state.items.find((item) => item.id === payload.id);
    if (!existing) throw new Error("Unknown slideshow");
    const changes = deepStripUndefined({
      title: payload.title.trim(),
      slides: payload.slides,
    });
    await updateDoc(doc(db, "slideshows", payload.id), {
      ...changes,
      updatedAt: serverTimestamp(),
    });
    const slideshow: Slideshow = { ...existing, ...changes };
    commit("UPSERT_ITEM", slideshow);
    return slideshow;
  },

  async remove(
    { commit }: ActionContext<SlideshowsState, RootState>,
    id: string,
  ) {
    await deleteDoc(doc(db, "slideshows", id));
    commit("REMOVE_ITEM", id);
  },
};

const getters = {
  slideshows(state: SlideshowsState): Slideshow[] {
    return state.items;
  },
  hasFetched(state: SlideshowsState): boolean {
    return (
      state.accountId != null && state.fetchedAccountId === state.accountId
    );
  },
  isFetching(state: SlideshowsState): boolean {
    return state.fetching;
  },
  error(state: SlideshowsState): string | null {
    return state.error;
  },
};

export const slideshows: Module<SlideshowsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
export default slideshows;
